import { observer, useLocalObservable } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IQuestionNode } from '@ifs-gcp/core';

import styles from '@/components/question/question/question.module.scss';
import type { FactoryElementProps } from '@/features/audit/audit-elements/factory-element';
import { ANSWER_COMPONENTS } from '@/features/audit/audit-elements/question-factory';
import { useAuditStore } from '@/store/auditStore';
import { useTouchedField } from '@/store/validationStore';
import type { BaseFactoryElementProps } from '@/types/question';
import { getTranslation } from '@/utils/translations';

import { getPlaceholderValue } from '../utils';

type AuditQuestionElementProps = {
  element: IQuestionNode;
  FactoryElement: React.FC<FactoryElementProps>;
};

export const AuditQuestionElement = observer(
  ({ element, FactoryElement }: AuditQuestionElementProps) => {
    const {
      i18n: { language },
    } = useTranslation();

    const {
      props: { id, placeholder, readOnlyCondition, disabledCondition, context },
    } = element;

    const audit = useAuditStore((state) => state.audit)!;

    const { isTouched, markTouched } = useTouchedField(id, context);

    const domain = useLocalObservable(() => audit.factory('question', element));

    const error = isTouched ? audit.status.getErrorByTemplate(element) : undefined;

    const setAnswer = React.useCallback(
      (value: unknown) => {
        domain.save(value);
      },
      [domain],
    );

    const Component = ANSWER_COMPONENTS[element.props.variant];

    if (!Component) {
      return null;
    }

    const renderFactoryElement = (props: Readonly<BaseFactoryElementProps>) => {
      // @ts-expect-error field
      return <FactoryElement {...props} />;
    };

    const { children = [] } = element;

    return (
      <div className={styles.question}>
        <Component
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          answer={domain.answer}
          error={getTranslation(error?.errors?.[0]?.error, language)}
          question={{
            ...element,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            props: {
              ...element.props,
              readOnlyCondition: readOnlyCondition && audit.conditions.validate(readOnlyCondition),
              disabledCondition: disabledCondition && audit.conditions.validate(disabledCondition),
              options: domain.options,
              placeholder: getPlaceholderValue(placeholder, language, audit.conditions),
            },
          }}
          language={language}
          onBlur={() => {
            markTouched();
          }}
          onChange={setAnswer}
          onAddInput={() => {}}
          onRemoveInput={() => {}}
          renderFactoryElement={renderFactoryElement}
        />

        {renderFactoryElement && Array.isArray(children) && Boolean(children.length) && (
          <div className={styles.wrapper}>
            {children.map((subQuestion) => {
              return renderFactoryElement({
                key: subQuestion.props.id,
                language,
                // @ts-expect-error field
                element: subQuestion,
              });
            })}
          </div>
        )}
      </div>
    );
  },
);
